import { getPromotionPricePerUnitInMicros } from './money';
import {
  ItemsContext_VariantsPackFieldsFragment,
  VolumePromotionsContext_PromotionEffectFieldsFragment,
  VolumePromotionsContext_PromotionQualificationFieldsFragment,
  VolumePromotionsContext_VolumePromotionFieldsFragment,
} from '@/types';
import {
  Promotion,
  PromotionDiscountType,
  PromotionEffectMetadata,
  PromotionQualificationMetadata,
  RestaurantPromotion,
  VariantPackVolumePromotion,
} from './volume-promotions-types';

interface MergedPromotionsMap {
  [key: string]: RestaurantPromotion;
}

export const mergePromotionsForRestaurant = (
  promotions: RestaurantPromotion[],
) => {
  const mergedPromotionsMap = promotions.reduce<MergedPromotionsMap>(
    (acc, promotionData) => {
      const {
        promotion,
        promotion_effect,
        promotion_qualification,
        variant_pack,
      } = promotionData;

      if (promotion) {
        const promotionUUID = promotion.uuid;
        if (!acc[promotionUUID]) {
          acc[promotionUUID] = {
            promotion,
            promotion_component_groups: [],
          };
        }

        const componentGroups = acc[promotionUUID].promotion_component_groups!;

        const componentGroupUUID =
          promotion_effect?.promotion_component_group_uuid ||
          promotion_qualification?.promotion_component_group_uuid;

        let componentGroup = componentGroups.find(
          (cg) => cg.uuid === componentGroupUUID,
        );
        if (!componentGroup) {
          componentGroup = {
            promotion_effect_variant_pack_ids: new Set(),
            promotion_qualifications: [],
            uuid: componentGroupUUID!,
            variant_packs: [],
          };
          componentGroups.push(componentGroup);
        }

        if (promotion_effect) {
          componentGroup.promotion_effect = {
            ...componentGroup.promotion_effect,
            ...promotion_effect,
          };

          if (variant_pack) {
            componentGroup.promotion_effect_variant_pack_ids.add(
              variant_pack.uuid,
            );
          }
        }

        if (
          promotion_qualification &&
          !componentGroup.promotion_qualifications.some(
            (q) => q.uuid === promotion_qualification.uuid,
          )
        ) {
          componentGroup.promotion_qualifications.push(promotion_qualification);
        }

        // Append unique variant packs to the component group
        if (
          variant_pack &&
          !componentGroup.variant_packs.some(
            (vp) => vp.uuid === variant_pack.uuid,
          )
        ) {
          componentGroup.variant_packs.push(variant_pack);
        }
      }
      return acc;
    },
    {},
  );

  return Object.values(mergedPromotionsMap);
};

export const adaptPromotion = ({
  background_color_end,
  background_color_start,
  background_image_url,
  discount_type,
  expires_at,
  hide_marketing_tile,
  hide_tagline,
  marketing_text,
  starts_at,
  supplier,
  tagline,
  uuid,
}: VolumePromotionsContext_VolumePromotionFieldsFragment): Promotion => {
  return {
    backgroundColorEnd: background_color_end,
    backgroundColorStart: background_color_start,
    backgroundImageUrl: background_image_url,
    discountType: discount_type as PromotionDiscountType,
    expiresAt: expires_at,
    hideMarketingTile: hide_marketing_tile,
    hideTagline: hide_tagline,
    marketingText: marketing_text,
    startsAt: starts_at,
    supplierTimezone: supplier.address.timezone,
    tagline,
    uuid,
  };
};

export const adaptPromotionEffect = (
  { photo_url_list }: VolumePromotionsContext_VolumePromotionFieldsFragment,
  promotion_effect:
    | VolumePromotionsContext_PromotionEffectFieldsFragment
    | null
    | undefined,
  variant_pack: ItemsContext_VariantsPackFieldsFragment,
) => {
  const effectMetadata = promotion_effect?.metadata as
    | PromotionEffectMetadata
    | undefined;
  const packPriceInMicros =
    typeof effectMetadata?.pack_price_in_micros === 'number'
      ? effectMetadata.pack_price_in_micros / variant_pack.pack.unit_count
      : null;

  return {
    discountInMicros: effectMetadata?.discount_in_micros,
    discountPercentage: effectMetadata?.discount_percentage,
    numAffected: effectMetadata?.num_affected,
    // Per-unit price of the promoted variant pack
    // v1 promotions set promotion.pack_price_in_micros, stored as per-unit on the promotion
    // v2 promotions that are not v1-compatible do not set promotion.pack_price_in_micros, instead storing it as
    // per-pack in the effect
    packPriceInMicros,
    photoURLs: photo_url_list ?? [],
    variantPack: variant_pack,
  };
};

export const isQualificationSatisfiedBySinglePack = (
  qualification: VolumePromotionsContext_PromotionQualificationFieldsFragment,
) => {
  const metadata = qualification.metadata as PromotionQualificationMetadata;
  if (typeof metadata.min_packs === 'number') {
    return metadata.min_packs === 1;
  }
  if (typeof metadata.exact_num_packs === 'number') {
    return metadata.exact_num_packs === 1;
  }
  return false;
};

export const getBestVariantPackVolumePromotion = (
  unitCount: number,
  promotions?: VariantPackVolumePromotion[],
  unitPriceInMicros?: number | null,
) => {
  let mostRecentlyStartedPromotion: VariantPackVolumePromotion | null = null;

  const result = (promotions || []).reduce<{
    promotionPricePerUnitInMicros: number | null;
    variantPackPromotion: VariantPackVolumePromotion | null;
  }>(
    (best, promotion) => {
      // Only consider promotions since other types like volume discounts or rebates
      // render their own label
      if (promotion.discountType !== PromotionDiscountType.Promotion) {
        return best;
      }

      // Track the most recently started promotion
      if (
        mostRecentlyStartedPromotion === null ||
        new Date(promotion.startsAt).getTime() >
          new Date(mostRecentlyStartedPromotion.startsAt).getTime()
      ) {
        mostRecentlyStartedPromotion = promotion;
      }

      // Do not consider promotions that have a minimum pack qualification
      if (!promotion.isSatisfiedBySinglePack) {
        return best;
      }

      const currentPrice = getPromotionPricePerUnitInMicros({
        basePriceInMicros: unitPriceInMicros,
        promotedVariantPackInfo: promotion.promotedVariantPackInfo,
        unitCount,
      });

      // Update best price promotion if we have a valid price
      if (
        currentPrice !== null &&
        (best.promotionPricePerUnitInMicros === null ||
          currentPrice < best.promotionPricePerUnitInMicros)
      ) {
        return {
          promotionPricePerUnitInMicros: currentPrice,
          variantPackPromotion: promotion,
        };
      }

      return best;
    },
    {
      promotionPricePerUnitInMicros: null,
      variantPackPromotion: null,
    },
  );

  if (
    mostRecentlyStartedPromotion &&
    result.promotionPricePerUnitInMicros === null &&
    result.variantPackPromotion === null
  ) {
    // Return the most recently started promotion if there is no best promotion
    result.variantPackPromotion = mostRecentlyStartedPromotion;
  }

  return result;
};
